// admin/events/EventDetailDrawer.jsx — now renders as a Modal
import React, { useState } from "react";
import Modal from "@/components/common/Modal";
import Badge from "@/components/common/Badge";
import Button from "@/components/common/Button";
import {
  Calendar, MapPin, Users, DollarSign, Route, Clock,
  Globe, Archive, ListChecks, CheckCircle, Milestone, EyeOff
} from "lucide-react";
import { getComponentLabel } from "@/utils/constants";
import EntityAuditHistory from "../audit/EntityAuditHistory";

const STATUS_BADGE = {
  submitted: { tone: "info", label: "Submitted", icon: <Clock className="w-3 h-3" /> },
  sponsor_listed: { tone: "organiser", label: "Sponsor Listed", icon: <ListChecks className="w-3 h-3" /> },
  published: { tone: "success", label: "Published", icon: <Globe className="w-3 h-3" /> },
  hidden: { tone: "warning", label: "Hidden", icon: <EyeOff className="w-3 h-3" /> },
  archived: { tone: "muted", label: "Archived", icon: <Archive className="w-3 h-3" /> },
};

const fmt = (d) => {
  if (!d) return "—";
  return new Date(d).toLocaleDateString("en-IN", {
    day: "numeric", month: "short", year: "numeric",
  });
};

const fmtDateTime = (d) => {
  if (!d) return "—";
  return new Date(d).toLocaleString("en-IN", {
    day: "numeric", month: "short", year: "numeric",
    hour: "2-digit", minute: "2-digit",
  });
};

const fmtType = (t) => t ? t.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()) : "—";

const Field = ({ label, value, icon: Icon }) => (
  <div>
    <p className="text-xs text-gray-500 dark:text-gray-400 mb-0.5 flex items-center gap-1">
      {Icon && <Icon className="w-3 h-3" />} {label}
    </p>
    <p className="text-sm font-medium text-gray-800 dark:text-gray-100">{value || "—"}</p>
  </div>
);

const EventDetailModal = ({ event, onClose, onOpenActions }) => {
  const [activeTab, setActiveTab] = useState("details");

  if (!event) return null;

  const badge = STATUS_BADGE[event.status] || STATUS_BADGE.submitted;

  return (
    <Modal
      open={true}
      onClose={onClose}
      title="Event Details"
      maxWidth="max-w-3xl"
      showCloseButton={false}
    >
      <div className="space-y-5">
        <div className="flex border-b border-gray-200 dark:border-gray-700 mb-4">
          <button
            className={`px-4 py-2 text-sm font-medium ${
              activeTab === "details"
                ? "text-green-600 border-b-2 border-green-600"
                : "text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
            }`}
            onClick={() => setActiveTab("details")}
          >
            Details
          </button>
          <button
            className={`px-4 py-2 text-sm font-medium ${
              activeTab === "history"
                ? "text-green-600 border-b-2 border-green-600"
                : "text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
            }`}
            onClick={() => setActiveTab("history")}
          >
            Audit History
          </button>
        </div>
        {activeTab === "details" ? (
          <div className="space-y-5">
            {/* Title & Status */}
        <div className="flex items-start justify-between gap-4">
          <div>
            <h4 className="text-lg font-semibold text-gray-800 dark:text-gray-100">
              {event.title}
            </h4>
            <p className="text-sm text-gray-500 mt-1">{fmtType(event.event_type)}</p>
          </div>
          <Badge tone={badge.tone}>
            <span className="flex items-center gap-1">{badge.icon} {badge.label}</span>
          </Badge>
        </div>

        {/* Description */}
        <div>
          <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Description</p>
          <p className="text-sm text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-gray-800 p-3 rounded-lg whitespace-pre-wrap">
            {event.description || "—"}
          </p>
        </div>

        {/* Details grid */}
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
          <Field label="Start Date" value={fmtDateTime(event.start_date)} icon={Calendar} />
          <Field label="End Date" value={fmtDateTime(event.end_date)} icon={Calendar} />
          <Field label="Capacity" value={event.capacity === 0 ? "Unlimited" : event.capacity} icon={Users} />
          <Field label="Entry Fee" value={event.is_paid ? `₹${event.entry_fee || 0}` : "Free"} icon={DollarSign} />
          <Field label="City" value={event.location?.city} icon={MapPin} />
          <Field label="State" value={event.location?.state_code} icon={MapPin} />
        </div>

        {/* Location address */}
        {event.location?.address && (
          <div>
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-0.5">Full Address</p>
            <p className="text-sm text-gray-700 dark:text-gray-300">{event.location.address}</p>
          </div>
        )}

        {/* Route Info */}
        {event.event_route && (
          <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
            <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase mb-2 flex items-center gap-1">
              <Route className="w-3 h-3" /> Route Information
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
              <Field label="Route Name" value={event.event_route.name} />
              <Field label="Distance" value={event.event_route.distance ? `${event.event_route.distance} km` : "—"} />
              <Field label="Start Point" value={event.event_route.start_point} />
              <Field label="End Point" value={event.event_route.end_point} />
            </div>
            {event.event_route.landmarks?.length > 0 && (
              <div className="mt-5">
                <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase mb-3 flex items-center gap-1">
                  <MapPin className="w-3 h-3" /> Landmarks ({event.event_route.landmarks.length})
                </p>
                <div className="relative pl-3 space-y-4 before:absolute before:inset-y-0 before:left-[17px] before:w-[2px] before:bg-gradient-to-b before:from-blue-100 before:via-gray-100 before:to-gray-100 dark:before:from-blue-900/30 dark:before:via-gray-700 dark:before:to-gray-700 pointer-events-none">
                  {event.event_route.landmarks.map((lm, i) => (
                    <div key={i} className="relative flex items-start gap-3 pointer-events-auto">
                      {/* Timeline Node */}
                      <div className="relative z-10 w-3 h-3 rounded-full bg-white dark:bg-gray-800 border-[2.5px] border-blue-400 dark:border-blue-500 shadow-[0_0_0_3px_white] dark:shadow-[0_0_0_3px_#1f2937] mt-[7px] shrink-0" />
                      
                      {/* Content */}
                      <div className="flex-1 bg-gray-50 dark:bg-gray-800/40 rounded-lg p-3 border border-gray-100 dark:border-gray-700/60 shadow-sm transition-all duration-300">
                        <div className="flex flex-wrap items-center justify-between gap-y-1 gap-x-3 mb-1">
                          <span className="font-semibold text-gray-900 dark:text-gray-100 text-[13px]">
                            {lm.name}
                          </span>
                          {lm.km_mark != null && (
                            <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/30 px-1.5 py-0.5 rounded border border-blue-100/50 dark:border-blue-800/30 shrink-0">
                              <Milestone className="w-3 h-3" />
                              At {lm.km_mark} km
                            </span>
                          )}
                        </div>
                        {lm.description && (
                          <p className="text-xs text-gray-500 dark:text-gray-400 leading-relaxed">
                            {lm.description}
                          </p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Organizer Info */}
        <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
          <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase mb-2">Organizer</p>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
            <Field label="Organization" value={event.organizer_id?.organization_name} />
            <Field label="Email" value={event.organizer_id?.email} />
          </div>
        </div>

        {/* Sponsorship Components */}
        {event.sponsorship_components?.length > 0 && (
          <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
            <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase mb-2">
              Sponsorship Components ({event.sponsorship_components.length})
            </p>
            <div className="space-y-2">
              {event.sponsorship_components.map((comp, i) => (
                <div key={i} className="flex items-center justify-between bg-gray-50 dark:bg-gray-800 p-2 rounded-lg">
                  <div>
                    <p className="text-sm font-medium text-gray-800 dark:text-gray-100">{getComponentLabel(comp.category)}</p>
                    {comp.description && <p className="text-xs text-gray-500">{comp.description}</p>}
                  </div>
                  {comp.type === "kind" ? (
                    <span className="text-xs font-semibold bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-300 px-2 py-1 rounded">
                      In-Kind
                    </span>
                  ) : comp.value != null && (
                    <span className="text-sm font-semibold text-green-700 dark:text-green-400">₹{comp.value}</span>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Audit Fields */}
        <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
          <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase mb-2">Audit Trail</p>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
            <Field label="Created At" value={fmtDateTime(event.created_at)} />
            <Field label="Submitted At" value={fmtDateTime(event.submitted_at)} />
            <Field label="Approved At" value={fmtDateTime(event.approved_at)} />
            {event.approved_by && (
              <Field label="Approved By" value={event.approved_by?.name || event.approved_by?.username || "—"} />
            )}
            {event.archived_at && (
              <Field label="Archived At" value={fmtDateTime(event.archived_at)} />
            )}
            {event.archived_by && (
              <Field label="Archived By" value={event.archived_by?.name || event.archived_by?.username || "—"} />
            )}
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex justify-end gap-3 pt-2 border-t border-gray-200 dark:border-gray-700">
          {event.status !== "archived" && (
            <Button variant="primary" size="sm" onClick={onOpenActions}>
              Manage Event
            </Button>
          )}
          <Button variant="outline" size="sm" onClick={onClose}>
            Close
          </Button>
        </div>
          </div>
        ) : (
          <div className="p-4 bg-gray-50/50 dark:bg-gray-800/20 rounded-lg">
            <EntityAuditHistory entityId={event._id} entityType="Event" />
          </div>
        )}
      </div>
    </Modal>
  );
};

export default EventDetailModal;
